// Service for the Attendance Dashboard "Empresa" filters panel (ASIS-5638 / REXASIS-001).
// GET /attendance/company-dashboard/filters — sections and companies catalogs.
// GET /attendance/company-dashboard/filters/areas — areas catalog, scoped to the
//   session company only (no query params — independent of the Empresa filter).
// GET /attendance/company-dashboard/filters/employees?companyIds=1,2&areaIds=3,4&... —
//   employees scoped to the selected companies/areas, paginated. Accepts an optional
//   employeeIds CSV (already-selected IDs) to validate against the whole universe when
//   pruning on a dimensional filter change — the response's validatedSelection field
//   drives that prune (mirrors /employees/filtered).

import { apiClient, API_CONFIG } from '@/api/core';
import type { ApiResponse } from '@/api/shared';
import { buildQueryString } from '@/api/shared';

import type {
  LC_GetCompanyDashboardFiltersOut,
  LC_GetCompanyDashboardAreasOut,
  LC_GetCompanyDashboardEmployeesOut,
  GetCompanyDashboardEmployeesParams,
  CompanyDashboardFilters,
  CompanyDashboardArea,
  CompanyDashboardEmployeesPage,
} from '../types/companyDashboardFilters.types';
import {
  mapCompanyDashboardFilters,
  mapCompanyDashboardAreas,
  mapCompanyDashboardEmployeesPage,
} from '../mappers/companyDashboardFilters.mapper';


export class CompanyDashboardFiltersService {
  async getFilters(signal?: AbortSignal): Promise<ApiResponse<CompanyDashboardFilters>> {
    const response = await apiClient.get<LC_GetCompanyDashboardFiltersOut>(
      API_CONFIG.ENDPOINTS.ATTENDANCE_COMPANY_DASHBOARD_FILTERS,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapCompanyDashboardFilters(response.data) };
  }

  async getAreas(signal?: AbortSignal): Promise<ApiResponse<CompanyDashboardArea[]>> {
    const response = await apiClient.get<LC_GetCompanyDashboardAreasOut>(
      API_CONFIG.ENDPOINTS.ATTENDANCE_COMPANY_DASHBOARD_FILTERS_AREAS,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapCompanyDashboardAreas(response.data) };
  }

  async getEmployees(
    params?: GetCompanyDashboardEmployeesParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<CompanyDashboardEmployeesPage>> {
    const query = buildQueryString({
      companyIds: params?.companyIds?.map(String),
      areaIds: params?.areaIds?.map(String),
      employeeIds: params?.employeeIds?.map(String),
      page: params?.page,
      pageSize: params?.pageSize,
      searchText: params?.searchText,
      orderBy: params?.orderBy,
      orderDirection: params?.orderDirection,
    });
    const endpoint = `${API_CONFIG.ENDPOINTS.ATTENDANCE_COMPANY_DASHBOARD_FILTERS_EMPLOYEES}${query}`;
    const response = await apiClient.get<LC_GetCompanyDashboardEmployeesOut>(endpoint, { signal });

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapCompanyDashboardEmployeesPage(response.data) };
  }
}

export const companyDashboardFiltersService = new CompanyDashboardFiltersService();
